import React, { useCallback } from 'react'
import toast from 'react-hot-toast'
import { useTranslations } from 'next-intl'

interface PasteImageButtonProps {
  onPaste: (file: File) => void
}

const PasteImageButton: React.FC<PasteImageButtonProps> = ({ onPaste }) => {
  const t = useTranslations('Component.PasteImageButton')

  const checkClipboardPermission = useCallback(async () => {
    try {
      const result = await navigator.permissions.query({ name: 'clipboard-read' as PermissionName })
      return result.state === 'granted'
    } catch (error) {
      return false
    }
  }, [])

  const handlePaste = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()

    const hasPermission = await checkClipboardPermission()
    if (!hasPermission) {
      toast.error(t('noPermission'))
      return
    }

    try {
      const items = await navigator.clipboard.read()
      if (!items.length) {
        toast.error(t('emptyClipboard'))
        return
      }
      const type = items[0].types.find(x => x.startsWith('image'))
      if (!type) {
        toast.error(t('notAnImage'))
        return
      }
      const blob = await items[0].getType(type)
      const file = new File([blob], `clipboard-${Math.random().toString().slice(2, 6)}.png`, {
        type: blob.type
      })
      onPaste(file)
    } catch (error) {
      console.error('Error pasting image:', error)
      toast.error(t('pasteError'))
    }
  }, [checkClipboardPermission, onPaste, t])

  return (
    <span
      className="italic font-medium text-orange-600 underline mr-2 cursor-pointer"
      onClick={handlePaste}
    >
      {t('pasteHere')}
    </span>
  )
}

export default PasteImageButton
